import React from 'react'
import { useAppDispatch } from '@SonarCX/hooks/redux'

import EditSingleUser from '../../../../components/molecules/EditSingleUser'
import UserListItem from '../UsersList/UserListItem'
import ShortName from './ShortName'

import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import useStyles from './styles'

type SingleUserProps = {
  taskID: string
  field: keyof LocalTask
  observerId: string
  type?: string
  disableEdit?: boolean
  external?: boolean
}

const SingleUser: React.FC<SingleUserProps> = ({
  taskID,
  field,
  type = 'Client',
  disableEdit,
  external,
  observerId
}) => {
  const dispatch = useAppDispatch()

  const { allUsers, loadUser } = useUserAPI()
  const { tasks, setSkipUpdateID, formatTask, updateTask } = useTaskAPI()

  const task = React.useMemo(() => tasks[taskID], [tasks, taskID])
  const [id, setID] = React.useState(task?.[field] as string)
  const [intersect, setIntersect] = React.useState(null)
  const [element, setElement] = React.useState(null)

  const user = React.useMemo(() => allUsers[id] ?? null, [allUsers, id])

  React.useEffect(() => {
    if (id && !user) {
      loadUser(id)
    }
  }, [id, user, loadUser])

  const classes = useStyles()

  // Prevent refreshing due to socket update
  const blockUpdate = React.useCallback(() => {
    setSkipUpdateID(old => [...old, task.id])
  }, [setSkipUpdateID, task])

  const handleAdd = React.useCallback(
    async (id: string) => {
      try {
        setID(id)
        blockUpdate()

        const newTask: LocalTask = {
          ...formatTask(task),
          [field]: id
        }
        await updateTask(newTask.id, newTask)
        dispatch(AddSuccessAlert('Changes saved'))
      } catch (err) {
        dispatch(AddErrorAlert({ err }))
      }
    },
    [blockUpdate, formatTask, task, field, updateTask, dispatch]
  )

  const handleDelete = React.useCallback(async () => {
    try {
      setID(null)
      blockUpdate()

      const newTask: LocalTask = {
        ...formatTask(task),
        [field]: null
      }
      await updateTask(newTask.id, newTask)
      dispatch(AddSuccessAlert('Changes saved'))
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }, [blockUpdate, formatTask, task, field, updateTask, dispatch])

  // observer variable to set viewport and check if user is in it
  const observer = React.useRef(
    new IntersectionObserver(
      entries => {
        setIntersect(entries[0])
      },
      { root: document.querySelector(`#${observerId}`), threshold: 0 }
    )
  )

  // effect for watching and unwatching observer variable
  React.useEffect(() => {
    const currentElement = element
    const currentObserver = observer?.current

    if (currentElement) {
      currentObserver.observe(currentElement)
    }

    // clean up observer to stop watching
    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement)
      }
    }
  }, [element])

  if (!id) {
    return (
      <div className={classes.root}>
        <EditSingleUser
          value={id}
          onChange={handleAdd}
          type={type}
          external={external}
        />

        <div className={classes.none}>No {type}</div>
      </div>
    )
  }

  if (!user) {
    return null
  }

  return (
    <div className={classes.root} ref={setElement}>
      <UserListItem
        user={user}
        onDelete={disableEdit ? undefined : handleDelete}
        disableTooltip
        intersect={intersect}
      />

      <ShortName name={user.name} />
    </div>
  )
}

export default React.memo(SingleUser)
